import { useEffect } from 'react'
import { create } from 'zustand'

export type EditorTab = "query" | "widget" | "styling" | "common"

/** Remembered across widget selections within the session; falls back when a widget lacks the tab. */
export const useEditorTabStore = create<{
  tab: EditorTab
  /** A field to focus as soon as it is rendered, which may take a config update (e.g. a just-added widget) */
  pendingFocus: { fieldId: string, until: number } | null
}>()(() => ({ tab: "query", pendingFocus: null }))

export function focusField(tab: EditorTab, fieldId: string) {
  useEditorTabStore.setState({ tab, pendingFocus: { fieldId, until: Date.now() + 1000 } })
}

/** Wraps the Query tab's query editors, so focusing it puts the cursor in the first one */
export const QUERY_EDITOR_ID = "widget-query-editor"

export function editIdentity() {
  focusField("common", "id-formfield")
}

/** Focuses the requested field once it is in the DOM; drops a request whose field never showed up. */
export function usePendingFocus() {
  const pendingFocus = useEditorTabStore(s => s.pendingFocus)
  useEffect(function focusRequestedField() {
    if (!pendingFocus) return
    let frame = 0
    let focusedOnce = false
    const attempt = () => {
      const field = focusableIn(document.getElementById(pendingFocus.fieldId))
      // A re-render can replace the field just after it got focus, which drops focus to the page
      // body. Refocus its latest instance then, but never take focus back from something the user chose.
      const lost = document.activeElement === document.body || document.activeElement == null
      if (field && field !== document.activeElement && (!focusedOnce || lost)) {
        field.focus()
        focusedOnce = true
      }
      if (Date.now() < pendingFocus.until) frame = requestAnimationFrame(attempt)
      else useEditorTabStore.setState({ pendingFocus: null })
    }
    attempt()
    return () => cancelAnimationFrame(frame)
  }, [ pendingFocus ])
}

/** Some fields put their id on a wrapper (a search select does), so focus the input inside it */
function focusableIn(element: HTMLElement | null): HTMLElement | null {
  if (!element || element.matches('input, textarea, select, button')) return element
  return element.querySelector<HTMLElement>('input, textarea, select') ?? element
}
